import { Injectable, inject, signal } from '@angular/core';
import { finalize, tap } from 'rxjs';

import {
  BookingChartDataDTO,
  BookingStatsDTO,
  ChartSearchParams,
  GroupedBarChartData,
  PieChartData,
  averagePeoplePerRT,
  averagePerWeekday,
  countPerMonthRT,
  countPerStatus,
} from '@bookly/shared';

import { ChartDataApiService } from './chart-data-api.service';

@Injectable({
  providedIn: 'root',
})
export class ChartDataService {
  readonly #chartApiService = inject(ChartDataApiService);
  readonly #chartData = signal<BookingChartDataDTO[]>([]);
  readonly #countPerMonthRT = signal<GroupedBarChartData[]>([]);
  readonly #averagePerWeekday = signal<GroupedBarChartData[]>([]);
  readonly #averagePeoplePerRoomType = signal<GroupedBarChartData[]>([]);
  readonly #countPerStatus = signal<PieChartData[]>([]);
  readonly #isDataLoading = signal<boolean>(false);
  readonly #areStatsLoading = signal<boolean>(false);
  readonly #stats = signal<BookingStatsDTO | null>(null);

  get chartData() {
    return this.#chartData.asReadonly();
  }
  get countPerMonthRT() {
    return this.#countPerMonthRT.asReadonly();
  }
  get averagePerWeekday() {
    return this.#averagePerWeekday.asReadonly();
  }
  get averagePeoplePerRoomType() {
    return this.#averagePeoplePerRoomType.asReadonly();
  }
  get countPerStatus() {
    return this.#countPerStatus.asReadonly();
  }
  get isDataLoading() {
    return this.#isDataLoading.asReadonly();
  }

  get areStatsLoading() {
    return this.#areStatsLoading.asReadonly();
  }

  get stats() {
    return this.#stats.asReadonly();
  }
  public loadChartData(queryParams?: ChartSearchParams) {
    this.#isDataLoading.set(true);
    return this.#chartApiService.getChartData(queryParams).pipe(
      tap(data => {
        this.#chartData.set(data);
        this.#countPerMonthRT.set(countPerMonthRT(data));
        this.#averagePerWeekday.set(averagePerWeekday(data));
        this.#averagePeoplePerRoomType.set(averagePeoplePerRT(data));
        this.#countPerStatus.set(countPerStatus(data));
      }),
      finalize(() => this.#isDataLoading.set(false))
    );
  }

  public loadBookingStats() {
    this.#areStatsLoading.set(true);
    return this.#chartApiService.getBookingsStats().pipe(
      tap(stats => this.#stats.set(stats)),
      finalize(() => this.#areStatsLoading.set(false))
    );
  }
}
